import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { Star } from "lucide-react";
import { fmtPct, fmtUsd, fmtNum, api } from "@/lib/api";
import { toast } from "sonner";
import { LiveStamp } from "@/components/LiveStamp";
import { FlashPrice } from "@/components/FlashPrice";
import { Hint } from "@/components/Hint";
import { SNAPSHOT_DELAYED_MS, ageLabel, sparkHistory } from "@/lib/freshness";

// 7-day sparkline drawn from timestamped [ts, price] points; when the provider sent no usable history the slot says so.
const Spark = ({ history, up, coinId }) => {
  const { points, asOf, reason } = history;
  if (!points.length) {
    return (
      <div style={{ width: 110, height: 36 }} className="flex items-center justify-center text-[10px] font-mono-ticker text-slate-600 border border-dashed border-slate-800 rounded"
        title="7-day price history is unavailable from the data provider right now." data-testid={`spark-unavailable-${coinId}`} data-reason={reason || "unavailable"}>
        No history
      </div>
    );
  }
  const d = points.map(([t, v]) => ({ t, v }));
  const color = up ? "#10B981" : "#EF4444";
  const asOfLabel = asOf ? new Date(asOf).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "unknown";
  return (
    <div style={{ width: 110, height: 36 }} title={`7-day hourly history · ${points.length} points · to ${asOfLabel}`} data-testid={`spark-${coinId}`} data-points={points.length} data-as-of={asOf || undefined}>
      <ResponsiveContainer>
        <AreaChart data={d}>
          <defs>
            <linearGradient id={`g${up ? "u" : "d"}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#g${up ? "u" : "d"})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Age of a CoinGecko snapshot row, shown only when no live/polled quote is overriding it.
const SnapshotStamp = ({ coin }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!coin.last_updated) return <span className="text-[10px] font-mono-ticker text-slate-500" data-testid={`row-snapshot-${coin.id}`} data-state="cached">cached price</span>;
  const age = now - Date.parse(coin.last_updated);
  const delayed = age > SNAPSHOT_DELAYED_MS;
  return (
    <span className={`text-[10px] font-mono-ticker ${delayed ? "text-amber-300" : "text-slate-500"}`} data-testid={`row-snapshot-${coin.id}`} data-state={delayed ? "delayed" : "snapshot"}
      title={delayed ? "The market-data provider has not refreshed this quote recently — this is the last price received, not a current one." : "CoinGecko snapshot; refreshed about every 10 min."}>
      {delayed ? "DELAYED · " : ""}updated {ageLabel(age)}
    </span>
  );
};

export const CoinRow = ({ coin, inWatchlist, onWatchChange, live }) => {
  const navigate = useNavigate();
  const up24 = (coin.price_change_percentage_24h ?? 0) >= 0;
  const history = sparkHistory(coin);
  const price = live?.price ?? coin.current_price;
  const [flash, setFlash] = useState(null);
  const prev = useRef(price);
  useEffect(() => {
    if (live?.price == null || prev.current == null || live.price === prev.current) { prev.current = live?.price ?? prev.current; return undefined; }
    setFlash(live.price > prev.current ? "up" : "down");
    prev.current = live.price;
    const id = setTimeout(() => setFlash(null), 500);
    return () => clearTimeout(id);
  }, [live?.price]);
  const flashCls = flash === "up" ? "text-emerald-300" : flash === "down" ? "text-red-300" : "";

  const toggleWatch = async (e) => {
    e.stopPropagation();
    try {
      if (inWatchlist) {
        await api.delete(`/watchlist/${coin.id}`);
        toast.success(`${coin.symbol?.toUpperCase()} removed from watchlist`);
      } else {
        await api.post("/watchlist", { coin_id: coin.id, symbol: coin.symbol, name: coin.name });
        toast.success(`${coin.symbol?.toUpperCase()} added to watchlist`);
      }
      onWatchChange?.();
    } catch {
      toast.error("Could not update watchlist");
    }
  };

  return (
    <button
      data-testid={`coin-row-${coin.id}`}
      onClick={() => navigate(`/coin/${coin.id}`)}
      className="w-full grid grid-cols-[auto_1fr_auto] lg:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_auto_auto] items-center gap-3 px-3 md:px-4 py-3 hover:bg-slate-800/40 border-b border-slate-900 text-left transition-colors"
    >
      <span className="text-xs font-mono-ticker text-slate-500 w-6">#{coin.market_cap_rank || "—"}</span>
      <div className="flex items-center gap-3 min-w-0">
        <img src={coin.image} alt="" className="w-7 h-7 rounded-full shrink-0" />
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{coin.name}</div>
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{coin.symbol}</div>
        </div>
      </div>
      <div className={`hidden lg:block min-w-0 font-mono-ticker text-sm text-right transition-colors duration-300 ${flashCls}`} data-testid={live ? `live-row-price-${coin.id}` : undefined}>
        {price != null ? <FlashPrice value={price} tid={`row-flash-${coin.id}`}>{fmtUsd(price)}</FlashPrice> : <span className="text-slate-500" data-testid={`row-price-unavailable-${coin.id}`} title="No quote is available from the market-data provider right now; nothing is shown rather than an invented price.">Price unavailable</span>}
        {live?.price != null && <span className={`inline-block w-1.5 h-1.5 rounded-full ml-1.5 align-middle ${live.state === "live" ? "bg-emerald-400 animate-pulse" : live.state === "polled" ? "bg-cyan-300" : live.state === "quiet" ? "bg-yellow-300" : "bg-amber-400"}`} title={live.state} />}
        {live?.price != null && <div className="hidden 2xl:flex justify-end"><LiveStamp className="whitespace-nowrap" at={live.at} state={live.state} reason={live.reason} tid={`row-stamp-${coin.id}`} /></div>}
        {live?.price == null && price != null && <div className="hidden 2xl:block"><SnapshotStamp coin={coin} /></div>}
      </div>
      <div className={`hidden lg:block font-mono-ticker text-sm text-right ${up24 ? "text-emerald-400" : "text-red-400"}`}>
        {fmtPct(coin.price_change_percentage_24h)}
      </div>
      <div className="hidden lg:block font-mono-ticker text-xs text-right text-slate-400">
        {fmtNum(coin.market_cap)}
      </div>
      <div className="hidden lg:block"><Spark history={history} up={up24} coinId={coin.id} /></div>
      <div className="lg:hidden text-right">
        <div className={`font-mono-ticker text-sm transition-colors duration-300 ${flashCls}`}>{price != null ? <FlashPrice value={price}>{fmtUsd(price)}</FlashPrice> : <span className="text-slate-500 text-xs">Price unavailable</span>}</div>
        <div className={`font-mono-ticker text-xs ${up24 ? "text-emerald-400" : "text-red-400"}`}>
          {fmtPct(coin.price_change_percentage_24h)}
        </div>
      </div>
      <Hint title={inWatchlist ? "Remove from watchlist" : "Add to watchlist"} text="Starred coins appear in Your Watchlist on the Dashboard." asChild>
      <span
        role="button"
        tabIndex={0}
        onClick={toggleWatch}
        onKeyDown={(e) => { if (e.key === "Enter") toggleWatch(e); }}
        data-testid={`watch-toggle-${coin.id}`}
        className="p-2 rounded-md hover:bg-slate-700/50 cursor-pointer"
        aria-label={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
      >
        <Star className={`w-4 h-4 ${inWatchlist ? "text-amber-400 fill-amber-400" : "text-slate-500"}`} />
      </span>
      </Hint>
    </button>
  );
};
